import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();
const INACTIVITY_LIMIT_MS = 60 * 60 * 1000; // 1 hour
const WARNING_LEAD_MS = 60 * 1000;          // show the warning 1 minute before expiry
const CHECK_INTERVAL_MS = 1000;
const ACTIVITY_STORAGE_KEY = "slh_agent_last_activity";

/**
 * 1-hour agent inactivity timeout. Tracks DELIBERATE interactions only
 * (click/keydown/touch — not passive mousemove/scroll, so the warning is a
 * meaningful "are you still there?") and surfaces a 60-second countdown
 * warning before forcing a logout. The backend enforces the same window
 * independently (the `agent_inactivity_middleware` in server.py, applied to
 * every /api/property-admin/* call) — this hook is the UX layer on top,
 * not the source of truth.
 *
 * Activity is also broadcast via localStorage so an idle dashboard tab
 * doesn't force-logout a shared session while another tab is actively
 * being used.
 */
export function useAgentSessionTimeout({ enabled = true, onExpire } = {}) {
  const lastActivityRef = useRef(Number(window.localStorage.getItem(ACTIVITY_STORAGE_KEY)) || Date.now());
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);

  const recordActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    try { window.localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now)); } catch { /* storage blocked — client-side only, harmless */ }
    setShowWarning(false);
  }, []);

  // Explicit "Stay logged in" action — pings a real endpoint so the
  // backend's own inactivity clock (last_activity) is refreshed too, not
  // just the client-side one.
  const stayLoggedIn = useCallback(async () => {
    try {
      await axios.get(`${API}/property-admin/me`, { withCredentials: true });
    } catch {
      // A genuinely dead session will 401 here — the next real API call
      // will surface that through the normal error handling anyway.
    }
    recordActivity();
  }, [recordActivity]);

  useEffect(() => {
    if (!enabled) return undefined;
    const events = ["mousedown", "keydown", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, recordActivity, { passive: true }));

    const onStorage = (e) => {
      if (e.key !== ACTIVITY_STORAGE_KEY || !e.newValue) return;
      const ts = Number(e.newValue);
      if (ts > lastActivityRef.current) {
        lastActivityRef.current = ts;
        setShowWarning(false);
      }
    };
    window.addEventListener("storage", onStorage);

    let expired = false;
    const interval = setInterval(() => {
      if (expired) return;
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= INACTIVITY_LIMIT_MS) {
        expired = true;
        setShowWarning(false);
        onExpireRef.current?.();
      } else if (idleFor >= INACTIVITY_LIMIT_MS - WARNING_LEAD_MS) {
        setShowWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((INACTIVITY_LIMIT_MS - idleFor) / 1000)));
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, recordActivity));
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
    };
  }, [enabled, recordActivity]);

  return { showWarning, secondsLeft, stayLoggedIn };
}
